/**
 * Settings → Policy → Network activity (PRD § 4): how recent network requests
 * were decided — ordinary allows, explicit grants, Web Risk blocks, explicit
 * blocks, overrides, checker failures — with the policy actions to manage
 * them. Every action here is the EXISTING machinery: allow = the ordinary
 * net grant (with the § 2.5 alarming variant when the row was Web Risk
 * flagged), block = the deny rule, unblock/revoke = the ordinary revoke by
 * natural key. No separate Web Risk surface.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

import { webRiskOverrideWarning } from "../../../../../shared/net-fetch-denial";
import {
  addNetGrant,
  blockOrigin,
  listActivity,
  listGrants,
  revokeGrant,
  type NetActivityEvent,
} from "../../../../policy/api";

export const Route = createFileRoute("/_workspace/settings/policy/activity")({
  component: ActivityPane,
});

const ACTIVITY_KEY = ["policy-net-activity"] as const;
const GRANTS_KEY = ["policy-grants"] as const;

/** The decision, in the member's terms. */
function decisionLabel(e: NetActivityEvent): {
  label: string;
  tone: "ok" | "warn" | "block";
} {
  if (e.decision === "allow") {
    return e.cause === "grant"
      ? { label: "Allowed · granted", tone: "ok" }
      : { label: "Allowed · not flagged by Web Risk", tone: "ok" };
  }
  switch (e.cause) {
    case "forbidden":
      return { label: "Blocked · never reachable", tone: "block" };
    case "blocked":
      return { label: "Blocked by a member", tone: "block" };
    case "webrisk-flagged":
      return { label: "Blocked · flagged by Google Web Risk", tone: "block" };
    case "webrisk-unavailable":
      return { label: "Denied · Web Risk couldn’t answer", tone: "warn" };
    case "structural":
      return { label: "Refused · invalid or private target", tone: "warn" };
    default:
      return { label: "Denied · no grant", tone: "warn" };
  }
}

const SOURCE_LABELS: Record<string, string> = {
  proxy: "App or member fetch",
  agent: "Heath (terminal)",
  app: "Background app",
  browser: "Managed browser",
};

function ActivityPane() {
  const activity = useQuery({ queryKey: ACTIVITY_KEY, queryFn: listActivity });

  return (
    <div className="mx-auto w-full max-w-3xl px-6 pt-8 pb-12">
      <h1 className="mb-1 text-lg font-semibold">Network activity</h1>
      <p className="mb-6 text-sm text-muted-foreground">
        What Hearth, apps, and Heath tried to reach, and how each request was
        decided. Rows roll up repeats of the same decision.
      </p>
      {activity.isLoading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : activity.error ? (
        <p className="text-sm text-destructive">
          {(activity.error as Error).message}
        </p>
      ) : activity.data && activity.data.events.length > 0 ? (
        <ul className="divide-y divide-border rounded-md border border-border">
          {activity.data.events.map((event) => (
            <ActivityRow key={event.id} event={event} />
          ))}
        </ul>
      ) : (
        <p className="text-sm text-muted-foreground">
          No network activity recorded yet. Decisions appear here as apps and
          Heath reach the network.
        </p>
      )}
    </div>
  );
}

function ActivityRow({ event }: { event: NetActivityEvent }) {
  const qc = useQueryClient();
  const [confirmOverride, setConfirmOverride] = useState(false);
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ACTIVITY_KEY });
    qc.invalidateQueries({ queryKey: GRANTS_KEY });
  };
  const allow = useMutation({
    mutationFn: () => addNetGrant(event.origin, {}),
    onSuccess: () => {
      setConfirmOverride(false);
      invalidate();
    },
  });
  const block = useMutation({
    mutationFn: () => blockOrigin(event.origin),
    onSuccess: invalidate,
  });
  const unblock = useMutation({
    mutationFn: () =>
      revokeGrant({
        domain: "net",
        principal: "agent:*",
        resource: event.origin,
      }),
    onSuccess: invalidate,
  });
  // The grants list tells the row what's currently in force (the activity row
  // itself is history, not state).
  const grants = useQuery({ queryKey: GRANTS_KEY, queryFn: listGrants });
  const current = grants.data?.grants.find(
    (g) => g.domain === "net" && g.resource === event.origin,
  );
  const blocked = current?.effect === "deny";
  const granted = current?.effect === "allow";
  const flagged = event.cause === "webrisk-flagged";
  // A structural refusal has no grantable origin, and a forbidden one can't be
  // granted by anyone: "Allow" would write a grant the rules ignore.
  const actionable =
    event.cause !== "structural" && event.cause !== "forbidden";

  const { label, tone } = decisionLabel(event);
  const busy = allow.isPending || block.isPending || unblock.isPending;

  return (
    <li className="flex flex-col gap-2 px-3 py-2.5">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <span className="break-all font-mono text-sm">{event.origin}</span>
        <Badge
          variant={
            tone === "block"
              ? "destructive"
              : tone === "warn"
                ? "secondary"
                : "outline"
          }
        >
          {label}
        </Badge>
        <span className="text-xs text-muted-foreground">
          {SOURCE_LABELS[event.source] ?? event.source}
          {event.count > 1 ? ` · ×${event.count}` : ""}
          {" · "}
          {new Date(event.lastAt).toLocaleString()}
        </span>
        {event.lastTarget ? (
          <span className="w-full break-all font-mono text-[11px] text-muted-foreground/80">
            last attempt: {event.lastTarget}
          </span>
        ) : null}
        <span className="ml-auto flex gap-1.5">
          {!actionable ? null : blocked ? (
            <Button
              variant="outline"
              size="sm"
              disabled={busy}
              onClick={() => unblock.mutate()}
            >
              Unblock
            </Button>
          ) : (
            <>
              {!granted ? (
                <Button
                  variant="outline"
                  size="sm"
                  disabled={busy}
                  onClick={() =>
                    flagged ? setConfirmOverride(true) : allow.mutate()
                  }
                >
                  Allow
                </Button>
              ) : (
                <Button
                  variant="outline"
                  size="sm"
                  disabled={busy}
                  onClick={() => unblock.mutate()}
                >
                  Revoke grant
                </Button>
              )}
              <Button
                variant="outline"
                size="sm"
                disabled={busy}
                onClick={() => block.mutate()}
              >
                Block
              </Button>
            </>
          )}
        </span>
      </div>
      {confirmOverride ? (
        <div className="rounded-md border border-destructive/40 bg-destructive/10 p-3">
          <p className="text-xs text-destructive">
            {webRiskOverrideWarning(event.origin, event.threatTypes)}
          </p>
          <div className="mt-2 flex gap-2">
            <Button
              variant="destructive"
              size="sm"
              disabled={busy}
              onClick={() => allow.mutate()}
            >
              Allow anyway (Web Risk override)
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={busy}
              onClick={() => setConfirmOverride(false)}
            >
              Cancel
            </Button>
          </div>
        </div>
      ) : null}
      {(allow.error ?? block.error ?? unblock.error) ? (
        <p className="text-xs text-destructive">
          {((allow.error ?? block.error ?? unblock.error) as Error).message}
        </p>
      ) : null}
    </li>
  );
}
